import type { TFunction } from 'i18next';

import type {
    BioscopeDataQuality,
    FeedingDashboardConfig,
    FeedingPen,
    FeedingSummary,
    PenWarning,
} from '@/domain/feedingDashboard';
import type { Locale } from '@/utils/localeFormatters';

import { surfaceQuality } from '../bioscopeQuality';
import { formatDashboardNumber } from '../dashboardNumber';
import type { KpiValue, KpiWarning } from '../FeedingKpiCard/FeedingKpiCard';
import type { KpiTone } from '../feedingChartColors';
import { warningConditions } from '../PenComparisonTable/penWarningText';
import { formatPercent, type PercentFormatOptions } from '../percentFormat';
import type { SystemLabels } from '../systemLabel';

export interface KpiCardProps {
    summary: FeedingSummary;
    /** Site config: the target when no pen is selected, and the configured system name. */
    config: FeedingDashboardConfig;
    /** The selected pens, for their own targets and the reasons a pen is left out. */
    pens: FeedingPen[];
    /** Display names that tell apart pens sharing a name (`penDisplayNames`). */
    penNames: ReadonlyMap<number, string>;
    /** The day every closing-day figure is evaluated on, as the page states it. */
    closingDay: string | null;
}

/** Joins a figure to its unit or comparator, so no line can break inside "3 208 g". */
export const NO_BREAK = '\u00A0';

/**
 * A figure in the dashboard locale (U3), with its unit joined by a no-break space, or "–"
 * when there is none.
 */
export function formatNumber(
    value: number | null,
    digits: number,
    locale: Locale,
    unit?: string
): string {
    if (value == null || !Number.isFinite(value)) return '–';
    const figure = formatDashboardNumber(value, locale, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
    return unit ? `${figure}${NO_BREAK}${unit}` : figure;
}

/** A headline figure and its unit apart, so the card can set the unit smaller. */
export function headline(
    value: number | null,
    digits: number,
    locale: Locale,
    unit: string
): Pick<KpiValue, 'text' | 'unit'> {
    const text = formatNumber(value, digits, locale);
    return text === '–' ? { text } : { text, unit: `${NO_BREAK}${unit}` };
}

/** `headline` for a percentage, keeping the locale's own join before "%" (none in en). */
export function headlinePercent(
    value: number | null,
    digits: number,
    options: PercentFormatOptions
): Pick<KpiValue, 'text' | 'unit'> {
    const formatted = formatPercent(value, digits, options);
    const split = /^(.*?)(\u00A0?%)$/.exec(formatted);
    return split ? { text: split[1], unit: split[2] } : { text: formatted };
}

/**
 * Each card rates its OWN Bioscope figure. FCR and RGI can be blank on a site with
 * perfectly good weights (an unmapped PMS signal kills the RGI denominator), and a
 * green dot there would sit beside a dash.
 */
export function cardQuality(summary: FeedingSummary, hasValue: boolean): BioscopeDataQuality {
    return surfaceQuality(summary.bioscopeDataQuality, hasValue);
}

/** A pellet fallthrough value's band: elevated from the lower threshold, high above the upper. */
export function pelletValueTone(tone: KpiTone): KpiValue['tone'] {
    if (tone === 'warning') return 'elevated';
    if (tone === 'alert') return 'high';
    return 'default';
}

/** The card's marker for a warned value, labelled with the condition it meets. */
export function kpiWarningMarker(
    warning: PenWarning | null,
    system: SystemLabels,
    t: TFunction,
    locale: Locale
): KpiWarning | undefined {
    if (!warning) return undefined;
    const [condition] = warningConditions([warning], system, t, locale);
    return {
        tier: warning.tier,
        label: t('feedingModule.table.warning.label', { conditions: condition }),
    };
}
